import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navbar from './Navbar';

import Imagee3 from "../src/assets/imgs1.jpeg";
import Imagee4 from "../src/assets/imgs2.jpeg";
import Imagee5 from "../src/assets/2024-25.jpg";
import Imagee6 from "../src/assets/2024-253.jpg";

/* ------------------------------------------------------------------
   Styles (kept in this file so the gallery is a single page/component)
------------------------------------------------------------------- */
const galleryStyles = `
@import url('https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,500;6..72,600&family=Hanken+Grotesk:wght@400;500;600&display=swap');

/* ---------- Tokens ---------- */
.ig-page {
  --ig-forest: #17352b;
  --ig-forest-soft: #24493c;
  --ig-paper: #f4f6f3;
  --ig-surface: #ffffff;
  --ig-ink: #1b2a25;
  --ig-muted: #5f6f68;
  --ig-line: #d8e0db;
  --ig-gold: #e0a100;

  --ig-serif: 'Newsreader', Georgia, 'Times New Roman', serif;
  --ig-sans: 'Hanken Grotesk', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

  min-height: 100vh;
  background: var(--ig-paper);
  color: var(--ig-ink);
  font-family: var(--ig-sans);
}

.ig-page *,
.ig-page *::before,
.ig-page *::after {
  box-sizing: border-box;
}

/* ---------- Main ---------- */
.ig-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px 72px;
}

/* ---------- Filters ---------- */
.ig-filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 36px;
}

.ig-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  font: 500 0.95rem var(--ig-sans);
  color: var(--ig-ink);
  background: var(--ig-surface);
  border: 1px solid var(--ig-line);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ig-chip:hover {
  border-color: var(--ig-forest-soft);
}

.ig-chip.is-active {
  background: var(--ig-forest);
  border-color: var(--ig-forest);
  color: #ffffff;
}

.ig-chip-count {
  min-width: 22px;
  padding: 1px 7px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  color: var(--ig-muted);
  background: #eef2ef;
  border-radius: 999px;
}

.ig-chip.is-active .ig-chip-count {
  color: var(--ig-forest);
  background: var(--ig-gold);
}

/* ---------- Sections ---------- */
.ig-section + .ig-section {
  margin-top: 56px;
}

.ig-section-head {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 14px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--ig-line);
}

.ig-section-title {
  margin: 0;
  font-family: var(--ig-serif);
  font-weight: 600;
  font-size: 1.7rem;
  color: var(--ig-forest);
}

.ig-section-meta {
  font-size: 0.9rem;
  color: var(--ig-muted);
}

.ig-empty {
  padding: 48px 0;
  text-align: center;
  color: var(--ig-muted);
}

/* ---------- Grid (larger photos) ---------- */
.ig-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
}

.ig-card {
  display: flex;
  flex-direction: column;
  flex: 0 1 400px;
  width: 400px;
  max-width: 100%;
  padding: 0;
  overflow: hidden;
  background: var(--ig-surface);
  border: 1px solid var(--ig-line);
  border-radius: 12px;
  cursor: zoom-in;
  font: inherit;
  text-align: left;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.ig-card:hover {
  border-color: var(--ig-forest-soft);
  box-shadow: 0 10px 28px rgba(23, 53, 43, 0.14);
}

.ig-card-media {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 10px;
  overflow: hidden;
  background: #eef2ef;
}

.ig-card-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #ffffff;
}

.ig-card-caption {
  display: block;
  padding: 12px 16px;
  text-align: center;
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--ig-ink);
  border-top: 1px solid var(--ig-line);
}

.ig-card:focus-visible,
.ig-chip:focus-visible,
.ig-viewer-btn:focus-visible {
  outline: 3px solid var(--ig-gold);
  outline-offset: 3px;
}

/* ---------- Photo viewer ---------- */
.ig-viewer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 72px;
  background: rgba(9, 20, 16, 0.94);
  animation: ig-fade 0.18s ease;
}

.ig-viewer-figure {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  max-height: 100%;
}

.ig-viewer-img {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 160px);
  object-fit: contain;
  border-radius: 6px;
  background: #ffffff;
}

.ig-viewer-caption {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 14px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #e7efeb;
}

.ig-viewer-counter {
  color: #9db3aa;
  font-weight: 400;
}

.ig-viewer-btn {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  padding: 0;
  font-size: 2rem;
  line-height: 1;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.ig-viewer-btn:hover {
  background: rgba(255, 255, 255, 0.24);
}

.ig-viewer-close {
  top: 16px;
  right: 16px;
}

.ig-viewer-prev {
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.ig-viewer-next {
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

@keyframes ig-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ---------- Responsive ---------- */
@media (max-width: 520px) {
  .ig-main {
    padding: 24px 16px 56px;
  }

  .ig-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .ig-viewer {
    padding: 64px 12px 72px;
  }

  .ig-viewer-prev,
  .ig-viewer-next {
    top: auto;
    bottom: 16px;
    transform: none;
  }

  .ig-viewer-prev {
    left: calc(50% - 56px);
  }

  .ig-viewer-next {
    right: calc(50% - 56px);
  }

  .ig-viewer-img {
    max-height: calc(100vh - 190px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ig-card,
  .ig-chip,
  .ig-viewer-btn {
    transition: none;
  }

  .ig-viewer {
    animation: none;
  }
}
`;

const ImageGallery = () => {
  const [images] = useState([
    { id: 4, url: Imagee3, year: '2024-2025' },
    { id: 5, url: Imagee4, year: '2024-2025' },
    { id: 6, url: Imagee5, year: '2025-2026' },
    { id: 7, url: Imagee6, year: '2025-2026' },
  ]);

  const [activeYear, setActiveYear] = useState('all');
  const [selectedId, setSelectedId] = useState(null);

  // Unique years, in the order they appear
  const years = useMemo(() => [...new Set(images.map((img) => img.year))], [images]);

  // Images shown for the current filter (also the viewer navigation order)
  const visibleImages = useMemo(
    () => (activeYear === 'all' ? images : images.filter((img) => img.year === activeYear)),
    [images, activeYear]
  );

  // Group visible images by year for section headings
  const groups = useMemo(
    () =>
      years
        .map((year) => ({
          year,
          items: visibleImages.filter((img) => img.year === year),
        }))
        .filter((group) => group.items.length > 0),
    [years, visibleImages]
  );

  const selectedIndex = visibleImages.findIndex((img) => img.id === selectedId);
  const selectedImage = selectedIndex >= 0 ? visibleImages[selectedIndex] : null;

  const closePopup = useCallback(() => setSelectedId(null), []);

  const showNext = useCallback(() => {
    if (selectedIndex < 0) return;
    setSelectedId(visibleImages[(selectedIndex + 1) % visibleImages.length].id);
  }, [selectedIndex, visibleImages]);

  const showPrev = useCallback(() => {
    if (selectedIndex < 0) return;
    setSelectedId(
      visibleImages[(selectedIndex - 1 + visibleImages.length) % visibleImages.length].id
    );
  }, [selectedIndex, visibleImages]);

  // Keyboard controls + lock page scroll while the viewer is open
  useEffect(() => {
    if (!selectedImage) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePopup();
      if (e.key === 'ArrowRight') showNext();
      if (e.key === 'ArrowLeft') showPrev();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedImage, closePopup, showNext, showPrev]);

  const countFor = (year) => images.filter((img) => img.year === year).length;

  return (
    <div className="ig-page">
      <style>{galleryStyles}</style>

      <Navbar />

      <main className="ig-main">
        <div className="ig-filters" role="tablist" aria-label="Filter photos by year">
          <button
            type="button"
            role="tab"
            aria-selected={activeYear === 'all'}
            className={`ig-chip ${activeYear === 'all' ? 'is-active' : ''}`}
            onClick={() => setActiveYear('all')}
          >
            All photos
            <span className="ig-chip-count">{images.length}</span>
          </button>
          {years.map((year) => (
            <button
              key={year}
              type="button"
              role="tab"
              aria-selected={activeYear === year}
              className={`ig-chip ${activeYear === year ? 'is-active' : ''}`}
              onClick={() => setActiveYear(year)}
            >
              {year}
              <span className="ig-chip-count">{countFor(year)}</span>
            </button>
          ))}
        </div>

        {groups.length === 0 && (
          <p className="ig-empty">No photos have been added yet.</p>
        )}

        {groups.map((group) => (
          <section key={group.year} className="ig-section">
            <div className="ig-section-head">
              <h2 className="ig-section-title">{group.year}</h2>
              <span className="ig-section-meta">
                {group.items.length} {group.items.length === 1 ? 'photo' : 'photos'}
              </span>
            </div>

            <div className="ig-grid">
              {group.items.map((image, i) => (
                <button
                  type="button"
                  key={image.id}
                  className="ig-card"
                  onClick={() => setSelectedId(image.id)}
                  aria-label={`Open ${image.year} photo ${i + 1}`}
                >
                  <span className="ig-card-media">
                    <img
                      src={image.url}
                      alt={`${image.year} photo ${i + 1}`}
                      className="ig-card-img"
                      loading="lazy"
                    />
                  </span>
                  <span className="ig-card-caption">
                    {image.year} &middot; Photo {i + 1}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </main>

      {selectedImage && (
        <div
          className="ig-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedImage.year} photo viewer`}
          onClick={closePopup}
        >
          <button
            type="button"
            className="ig-viewer-btn ig-viewer-close"
            onClick={closePopup}
            aria-label="Close photo viewer"
          >
            &times;
          </button>

          {visibleImages.length > 1 && (
            <button
              type="button"
              className="ig-viewer-btn ig-viewer-prev"
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
              aria-label="Previous photo"
            >
              &#8249;
            </button>
          )}

          <figure className="ig-viewer-figure" onClick={(e) => e.stopPropagation()}>
            <img
              key={selectedImage.id}
              src={selectedImage.url}
              alt={`${selectedImage.year} photo`}
              className="ig-viewer-img"
            />
            <figcaption className="ig-viewer-caption">
              <span>{selectedImage.year}</span>
              <span className="ig-viewer-counter">
                {selectedIndex + 1} of {visibleImages.length}
              </span>
            </figcaption>
          </figure>

          {visibleImages.length > 1 && (
            <button
              type="button"
              className="ig-viewer-btn ig-viewer-next"
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              aria-label="Next photo"
            >
              &#8250;
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;